/**
 * @fileoverview Calendar — section overview route
 *
 * URL: /dashboard/jobs/:jobId/calendar/overview
 *
 * Landing page for the "Calendar" sidebar accordion group. Mirrors the
 * Gather Information / Data / Investigations overviews: a titled `bg-muted`
 * page with a single grouped card whose rows link out to the calendar's three
 * surfaces — Key Dates, Tasks, and Review Windows — with live at-a-glance
 * counts so the practitioner sees what's outstanding before drilling in.
 */

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import {
  CalendarDays,
  Scale,
  SquareKanban,
  type LucideIcon,
} from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { isEventKind } from "@/calendar/eventAdapter";
import { Button } from "@/ui/primitives/button";

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/jobs/$jobId/calendar/overview",
)({
  component: CalendarOverviewPage,
});

type CalendarSection = {
  label: string;
  description: string;
  icon: LucideIcon;
  to:
    | "/dashboard/jobs/$jobId/calendar"
    | "/dashboard/jobs/$jobId/calendar/tasks"
    | "/dashboard/jobs/$jobId/calendar/review-windows";
  count: number | null;
  countLabel: string;
};

function CalendarOverviewPage() {
  const { jobId } = Route.useParams();
  const typedJobId = jobId as Id<"jobs">;
  const navigate = useNavigate();

  const { data: events } = useQuery(
    convexQuery(api.keyDates.queries.listForJob, { jobId: typedJobId }),
  );

  // Lightweight rollups so the overview is informative, not just a menu.
  // Key dates == every event-treatment row: statutory/derived rows carry
  // `kind: "task"` for legacy reasons, so use the shared `isEventKind` helper
  // (isDerived || code || kind !== "task") rather than a raw `kind` check.
  const rows = events ?? [];
  const keyDateCount = rows.filter((e) => isEventKind(e)).length;
  const openTaskCount = rows.filter(
    (e) =>
      !isEventKind(e) &&
      e.statusType !== "completed" &&
      e.statusType !== "canceled",
  ).length;
  const overdueCount = rows.filter(
    (e) => e.overdue && e.category !== "appointment",
  ).length;

  const sections: ReadonlyArray<CalendarSection> = [
    {
      label: "Tasks",
      description:
        "Plan & track the work for this matter in a board or list view. Assign, prioritise & plan tasks for your job.",
      icon: SquareKanban,
      to: "/dashboard/jobs/$jobId/calendar/tasks",
      count: events ? openTaskCount : null,
      countLabel: "open",
    },
    {
      label: "Calendar",
      description:
        "Key dates are automatically created for job. Manage all events related to the job.",
      icon: CalendarDays,
      to: "/dashboard/jobs/$jobId/calendar",
      count: events ? keyDateCount : null,
      countLabel: "events",
    },
    {
      label: "Review Windows",
      description:
        "Preference, uncommercial, related-party, and PPSA-vesting recovery windows for the appointment.",
      icon: Scale,
      to: "/dashboard/jobs/$jobId/calendar/review-windows",
      count: null,
      countLabel: "",
    },
  ];

  return (
    <div className="flex flex-1 flex-col gap-5 bg-muted p-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          Tasks &amp; Calendar
        </h1>
        <p className="text-sm text-muted-foreground">
          Task planning, calendar events and statutory review windows for this
          job.
          {events && overdueCount > 0 ? (
            <span className="ml-1 font-medium text-red-600 dark:text-red-400">
              {overdueCount} item{overdueCount === 1 ? "" : "s"} overdue.
            </span>
          ) : null}
        </p>
      </div>

      <div className="overflow-hidden rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)]">
        <div className="flex items-baseline justify-between px-5 py-3">
          <h4 className="text-sm font-semibold">Sections</h4>
        </div>
        <div className="divide-y divide-border">
          {sections.map((section) => (
            <SectionRow
              key={section.label}
              section={section}
              onOpen={() => navigate({ to: section.to, params: { jobId } })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** One row inside the grouped card (icon → title + blurb + count → Open). */
function SectionRow({
  section,
  onOpen,
}: {
  section: CalendarSection;
  onOpen: () => void;
}) {
  const Icon = section.icon;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      className="group w-full cursor-pointer bg-background text-left transition-colors duration-150 hover:bg-muted/40"
    >
      <div className="flex items-center gap-4 px-5 py-3.5">
        <div className="shrink-0">
          <div className="flex size-8 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
            <Icon className="size-4 text-emerald-600 dark:text-emerald-500" />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <span className="text-sm font-semibold text-foreground">
              {section.label}
            </span>
            {section.count !== null && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
                {section.count} {section.countLabel}
              </span>
            )}
          </div>
          <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
            {section.description}
          </p>
        </div>

        <div
          className="flex shrink-0 items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Button size="sm" onClick={onOpen} className="h-9 gap-1.5">
            Open
          </Button>
        </div>
      </div>
    </div>
  );
}
